import { env } from '../env';

/**
 * Helix config handed to the embedded PIXI game as `initData.helix`
 * (see the game repo: src/utils/HelixAnalytics.js and docs/PERF_TELEMETRY.md).
 *
 * - No HELIX_WRITE_KEY -> returns undefined -> the game's Helix tracker stays
 *   completely off (its "off unless configured" contract).
 * - HELIX_PERF=true additionally enables the game's performance telemetry
 *   (perf_* events: long frames, stalls, attribution). Dev/stage only —
 *   production must never set it, or thousands of low-end devices will spam
 *   Helix with perf events.
 *
 * One helper because three components build game init data (GameTable.web,
 * GameTable.android, TutorialTable.web) — the config must not drift.
 */
export interface GameHelixConfig {
	url: string;
	projectId: string;
	writeKey: string;
	perf?: boolean;
}

/** env() throws on a missing key; these HELIX_ vars are genuinely optional. */
function optionalEnv(key: string): string | undefined {
	try {
		return env(key, '') || undefined;
	} catch {
		return undefined;
	}
}

export function gameHelixConfig(): GameHelixConfig | undefined {
	const writeKey = optionalEnv('HELIX_WRITE_KEY');
	const perf = optionalEnv('HELIX_PERF') === 'true';
	// perf without a writeKey is still meaningful: the game's monitor runs
	// and mirrors perf events to the console (nothing is sent anywhere).
	if (!writeKey && !perf) {
		return undefined;
	}
	return {
		url: optionalEnv('HELIX_URL') || 'https://helix.awd-group.tech',
		projectId: optionalEnv('HELIX_PROJECT_ID') || 'svara',
		writeKey: writeKey || '',
		perf,
	};
}
